import { type RefObject, useEffect, useMemo, useRef } from 'react';
import type { ViewerAnimState } from '@mindcloudxai_3d/core';
import type { RendererController } from '@renderer/index';
import type { ExploreUrlParams } from '@hooks/useUrlParams';
import { useViewerStore } from '@stores/viewerStore';
import { generateMockCinematicEvents } from '@/modules/cinematic/mockTracks';
import type { CinematicEvent } from '@app-types/scene';

/**
 * 监听 anim 时间推进，当时间跨越事件关键帧时触发对应动作。
 *
 * 纯"获取式"实现——不驱动 viewer，只读取时间回流：
 * - viewer 是运镜唯一的播放/时间权威，hook 仅在 anim:changed 回调里做边界检测
 * - 仅在正向推进（currentTime > prevTime）时触发，规避 seek 回退与 loop 跨零点的重复触发
 * - lastTime 在 events/会话切换时归零，新会话从头开始判定
 *
 * 与本仓库其他 explore hook 一致采用 ref 模式，因为 controller 在 ExplorePage
 * 重建会话时由 effect 内部切换，没有走 React state；通过 `status === 'ready'`
 * 触发重新订阅。
 */
export function useCinematicEvents(
  controllerRef: RefObject<RendererController | null>,
  urlParams: ExploreUrlParams,
) {
  const { mockTrack, trackDuration } = urlParams;
  const sceneConfig = useViewerStore((s) => s.sceneConfig);
  const status = useViewerStore((s) => s.status);
  const lastTimeRef = useRef(0);

  // mock 优先于后端事件序列
  const events = useMemo<CinematicEvent[] | undefined>(() => {
    if (mockTrack) return generateMockCinematicEvents(mockTrack, trackDuration);
    return sceneConfig?.cinematicEvents;
  }, [mockTrack, trackDuration, sceneConfig?.cinematicEvents]);

  useEffect(() => {
    if (status !== 'ready') return;
    const ctrl = controllerRef.current;
    if (!ctrl || !events?.length) return;

    lastTimeRef.current = 0;

    const handler = (anim: ViewerAnimState) => {
      const prevTime = lastTimeRef.current;
      const currentTime = anim.time;
      lastTimeRef.current = currentTime;

      // 非正向推进（seek 回退、loop 跨零点）跳过，避免重复触发
      if (currentTime <= prevTime) return;

      for (const ev of events) {
        if (ev.t > prevTime && ev.t <= currentTime) {
          if (ev.type === 'hotspot' && typeof ev.payload?.index === 'number') {
            useViewerStore.getState().setActiveHotspotIndex(ev.payload.index);
          }
          // 'custom' 暂留扩展点，由 payload 自描述，这里不做内置分发
        }
      }
    };

    ctrl.on('anim:changed', handler);
    return () => {
      ctrl.off('anim:changed', handler);
    };
  }, [controllerRef, events, status]);
}
